/**
 * SimulationPanelView — interactive simulation/eval conversation
 * rendered inside the AoA inspector panel.
 *
 * Why this component exists:
 *   The Agent of Agents calls `send_test_message_to_agent(chatbot_id, ...)`
 *   to test how a target agent behaves. The result is a normal
 *   conversation row (eval_conversation_id) with the user/assistant
 *   message pair persisted in the standard `conversations` + `messages`
 *   tables. This panel surfaces that conversation so the user can
 *   continue the eval directly (typing as the user, the target agent
 *   replies) using the exact same UI as the regular chat page.
 *
 * Design rules:
 *   - REUSE the regular chat's `MessageList` and `ChatInput`. The
 *     simulation should feel identical to talking to that agent on the
 *     normal chat page — no new chrome, no new style.
 *   - New simulations REPLACE the active slot (see InspectorContext).
 *     This component is keyed by `evalConversationId` in the parent so
 *     a new simulation triggers a full remount, clearing local state.
 *   - The user is sending messages to the target chatbot via the
 *     normal `/conversations/<id>/stream` endpoint — the eval
 *     conversation is a regular conversation with a `chatbot_id` set
 *     by the original send_test_message_to_agent call.
 *   - Model picker is hidden (the agent's own default model wins) and
 *     attachments are disabled (eval conversations are message-only
 *     for now — file upload UX in this narrow panel is unproven).
 *
 * Phase 3 hooks:
 *   - `scrollToMessageId` will be honoured: after messages load, scroll
 *     the matching bubble into view and highlight it briefly. Wired
 *     here so the tool-call-block click can jump to the specific turn
 *     the user is curious about.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import api from '@/services/api';
import conversationService from '@modules/conversations/services/conversationService';
import { MessageList } from '@modules/conversations/components/MessageList';
import { ChatInput } from '@modules/conversations/components/ChatInput';
import { modelCatalogUrl } from '@/utils/modelPicker';
import { useLanguage } from '@hooks/useLanguage';
import { useInspector } from './InspectorContext';
import { aoaApi } from '../services/aoaApi';
import { TenantHierarchyPicker, type TenantOption } from './TenantHierarchyPicker';
import { BotAvatar } from '@/components/common/BotAvatar';
import botService from '@modules/chatbots/services/botService';
import type { Message } from '@/types/models';
import './SimulationPanelView.css';

interface Props {
  evalConversationId: string;
  targetChatbotId: string | null;
  targetAgentName: string;
  /** When set, scroll to this message after the initial load and pulse
   *  a highlight so the user can spot what they clicked to open the
   *  panel. Phase 3. */
  scrollToMessageId?: string | null;
}

/** Stable id used for the streaming placeholder message in local state.
 *  Replaced by the real message_id from the `ai_complete` event. */
const TEMP_AI_ID = 'sim-temp-ai';
/** The target agent's reply streaming in for an AoA-driven turn. Replaced by
 *  the persisted row the moment the refetch lands (never merged as a
 *  leftover optimistic bubble, or the reply would show twice). */
const AOA_DRAFT_ID = 'sim-temp-ai-aoa';

export const SimulationPanelView: React.FC<Props> = ({
  evalConversationId,
  targetChatbotId,
  targetAgentName,
  scrollToMessageId,
}) => {
  const { t } = useLanguage();
  // The panel can open a moment before the target agent's name is known (the
  // meta-agent fires the tool, the name lookup lands just after). Render a
  // neutral header in that gap rather than a placeholder identity — showing the
  // wrong name and a colour-hashed avatar for it, then swapping, reads as a bug.
  const agentKnown = Boolean((targetAgentName || '').trim());
  const displayAgentName = agentKnown ? targetAgentName : t('metaAgent.simulation.preparing');

  const {
    simulationRefreshNonce,
    pendingUserMessageByEvalId,
    toolInFlightEvalIds,
    setToolInFlight,
    clearPendingUserMessage,
    simulationDraftByEvalId,
    clearSimulationDraft,
    proposalQueue,
    toggleCollapsed,
    setActiveProposal,
    setActiveSimulation,
  } = useInspector();
  const queueLen = proposalQueue.length;
  const [queueOpen, setQueueOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [streaming, setStreaming] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  // Optimistic state for the CURRENT eval — the AoA-driven user message
  // we should render as a bubble before the tool result lands, plus
  // whether the tool is still running (so we render a "typing" hint).
  const aoaPendingUserMessage = pendingUserMessageByEvalId[evalConversationId];
  const aoaDraft = simulationDraftByEvalId[evalConversationId] || '';
  const toolInFlight = toolInFlightEvalIds.has(evalConversationId);
  // Currently-selected model for the panel's ChatInput picker. Sync'd
  // to the most recently-used model on this eval conversation (the
  // model the last assistant turn actually ran on, persisted on
  // `message.model_name`). Bidirectional sync with the AoA: the
  // backend pins `eval_override_model` in `conversation_context` from
  // both the AoA tool path and the /conversations route, so picker
  // selection in the panel propagates to the next AoA tool turn, and
  // an AoA-driven model choice flows back into this picker. Users
  // can still override mid-conversation by clicking the picker — the
  // change writes through to the conversation_context on the next
  // send and the AoA inherits it.
  const [selectedModel, setSelectedModel] = useState<string>('');
  // Has the user touched the picker since the last messages-load?
  // Their pick wins over auto-sync until the next AoA-driven turn
  // lands a new assistant message (at which point we re-sync to that
  // assistant's model — the persisted truth).
  const userPickedModelRef = useRef<boolean>(false);
  // The agent's own allowed_models list — drives the ChatInput's
  // picker so the user only sees models THIS agent can run on (not
  // every tenant-allowed model). Loaded once per target agent.
  // `'all'` means "no agent-level filter, use whatever the tenant
  // allows" — same convention BotModal uses.
  const [agentAllowedModels, setAgentAllowedModels] = useState<string | string[]>('all');
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const streamCancelRef = useRef<(() => void) | null>(null);

  // Family catalog from /ai/models/ — pickers deal exclusively in FAMILY
  // keys ('sonnet', 'gpt-mini', ...), but persisted assistant messages
  // carry the CONCRETE model that actually ran (message.model_name), and
  // the agent's stored allowed_models may still hold legacy concrete ids.
  // `normalizeModelRef` maps any ref back to its family (via `id` OR
  // `resolved_model`) so the picker's selection always lines up with the
  // family entries ChatInput loads. Unmatched refs (retired versions the
  // backend still resolves) pass through unchanged.
  const [modelFamilies, setModelFamilies] = useState<
    Array<{ id: string; name: string; resolved_model?: string }>
  >([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // The simulated tenant's pins, as the composer below uses.
        const response = await api.get(modelCatalogUrl(null, {
          conversationId: evalConversationId.startsWith('pending-') ? null : evalConversationId,
        }));
        const list = (response.data.models || []).map((m: any) => ({
          id: m.id,
          name: m.name || m.id,
          resolved_model: m.resolved_model || undefined,
          version_ids: m.version_ids || undefined,
        }));
        if (!cancelled) setModelFamilies(list);
      } catch (e) {
        // Non-fatal — the picker still works, selection just won't be
        // normalized for legacy concrete refs.
        console.warn('[AoA SimulationPanel] could not load model families', e);
      }
    })();
    return () => { cancelled = true; };
  }, [evalConversationId]);
  const familyByRef = useMemo(() => {
    const m = new Map<string, { id: string; name: string }>();
    for (const f of modelFamilies) {
      m.set(f.id, f);
      if (f.resolved_model) m.set(f.resolved_model, f);
    }
    return m;
  }, [modelFamilies]);
  const normalizeModelRef = useCallback(
    (ref: string): string => familyByRef.get(ref)?.id ?? ref,
    [familyByRef],
  );

  // Load the target agent's allowed_models when the panel first opens
  // for a new chatbot. ChatInput will intersect this with the tenant's
  // model whitelist on its own when it loads /ai/models/. The stored
  // entries may be FAMILY keys or legacy concrete ids — passed through
  // as-is because /ai/models/?allowed_models= normalizes both to
  // families server-side.
  useEffect(() => {
    if (!targetChatbotId) return;
    let cancelled = false;
    (async () => {
      try {
        const bot = await botService.getBot(targetChatbotId);
        if (cancelled) return;
        const stored = (bot as any)?.handler_config?.allowed_models;
        if (stored === undefined || stored === null || stored === 'all') {
          setAgentAllowedModels('all');
        } else if (Array.isArray(stored)) {
          setAgentAllowedModels(stored.length > 0 ? stored : 'all');
        } else if (typeof stored === 'string') {
          // Legacy comma-separated string. Split + trim.
          const parts = stored.split(',').map((s) => s.trim()).filter(Boolean);
          setAgentAllowedModels(parts.length > 0 ? parts : 'all');
        } else {
          setAgentAllowedModels('all');
        }
      } catch (e) {
        // Non-fatal: fall back to 'all' (tenant filter still applies in ChatInput).
        console.warn('[AoA SimulationPanel] could not load agent allowed_models', e);
      }
    })();
    return () => { cancelled = true; };
  }, [targetChatbotId]);

  // Some eval ids are SYNTHETIC placeholders (`pending-<chatbot>`) used
  // before the real eval_conversation_id has come back from the tool.
  // In that case there's nothing to fetch — the panel just renders
  // the optimistic user bubble + typing indicator until the real id
  // replaces it.
  const isPendingPlaceholder = evalConversationId.startsWith('pending-');

  // ---- Simulation tenant picker ----
  // Which tenant's organizational context this simulation runs UNDER. The
  // picker lets the user run the SAME agent exactly as its real users in
  // different tenants experience it (hierarchical context + tenant files +
  // tenant bundles). Changing the tenant starts a FRESH eval bound to that
  // tenant (the transcript resets — a different tenant is a different run).
  const [simTenants, setSimTenants] = useState<Array<TenantOption & { is_home?: boolean }>>([]);
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const [switchingTenant, setSwitchingTenant] = useState(false);

  // The panel no longer has a "setup" mode. Choosing the tenant happens on the
  // `propose_simulation_tenant` card in the chat, which creates the eval before
  // it ever opens this panel — so by the time we render, there is always a real
  // run to render.

  // Load the tenants this user may simulate for the target agent.
  useEffect(() => {
    if (!targetChatbotId) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await aoaApi.getSimulatableTenants(targetChatbotId);
        if (!cancelled) setSimTenants(rows);
      } catch (e) {
        console.warn('[AoA SimulationPanel] could not load simulatable tenants', e);
      }
    })();
    return () => { cancelled = true; };
  }, [targetChatbotId]);

  // Reflect the CURRENT eval's sim tenant in the picker.
  useEffect(() => {
    if (isPendingPlaceholder || !evalConversationId) return;  // setup mode has no eval yet
    let cancelled = false;
    (async () => {
      try {
        const ev = await aoaApi.getEvaluation(evalConversationId);
        if (!cancelled && ev?.sim_tenant_id) setSelectedTenantId(String(ev.sim_tenant_id));
      } catch { /* non-fatal — picker just shows no selection */ }
    })();
    return () => { cancelled = true; };
  }, [evalConversationId, isPendingPlaceholder]);

  const handleTenantChange = useCallback(async (tenantId: string) => {
    if (!targetChatbotId || !tenantId || tenantId === selectedTenantId) return;
    setSwitchingTenant(true);
    setSelectedTenantId(tenantId);  // optimistic
    try {
      const evalRow = await aoaApi.createEvaluation({
        chatbot_id: targetChatbotId,
        tenant_id: tenantId,
      });
      // Switch the panel to the fresh, tenant-bound eval. Same target agent →
      // the panel (keyed on chatbot) stays mounted and just resets messages.
      setActiveSimulation({
        kind: 'simulation',
        eval_conversation_id: evalRow.conversation_id,
        target_chatbot_id: targetChatbotId,
        target_agent_name: targetAgentName,
      });
    } catch (e) {
      console.error('[AoA SimulationPanel] tenant switch failed', e);
    } finally {
      setSwitchingTenant(false);
    }
  }, [targetChatbotId, selectedTenantId, targetAgentName, setActiveSimulation]);

  // The panel is keyed on target_chatbot_id (see AoaInspectorPanel),
  // so the component does NOT remount when evalConversationId flips
  // from `pending-<x>` to its real UUID — that's deliberate, the
  // placeholder→real transition must preserve the optimistic user
  // bubble (otherwise both bubbles render fresh on the remount and
  // the user perceives the whole turn re-rendering).
  // BUT: when the user explicitly starts a new eval against the same
  // target ("start over"), evalConversationId moves between two REAL
  // ids and the old conversation's messages must NOT spill into the
  // new one. Detect that transition here and reset local state.
  const prevEvalIdRef = useRef<string>(evalConversationId);
  useEffect(() => {
    const prev = prevEvalIdRef.current;
    prevEvalIdRef.current = evalConversationId;
    if (prev === evalConversationId) return;
    const wasPlaceholder = prev.startsWith('pending-') || prev === '';  // '' = setup mode
    const isPlaceholderNow = evalConversationId.startsWith('pending-');
    // Reset only on real→real transitions. Placeholder→real and setup→real
    // KEEP the optimistic / streamed bubbles (the merge reconciles them with
    // the server's persisted rows) so the run doesn't visibly flash-reload.
    if (!wasPlaceholder && !isPlaceholderNow) {
      setMessages([]);
      // prevMessageCountRef self-corrects on the next render via the
      // scroll-tracker effect below — no manual reset needed.
    }
  }, [evalConversationId]);

  // Load (or refresh) the eval's messages. Fires on:
  //   - mount / eval id change           → full reload, blank → messages
  //   - simulationRefreshNonce bump      → AoA fired another turn for
  //                                        the same eval; pull in the
  //                                        freshly-persisted messages
  // Skipped entirely while the id is a placeholder.
  useEffect(() => {
    if (isPendingPlaceholder || !evalConversationId) {
      // Nothing to GET yet — a `pending-<chatbot>` placeholder or setup mode
      // (empty id, before the user presses Run). Settle loading immediately so
      // the panel renders the optimistic / setup state instead of a spinner.
      setLoading(false);
      setLoadError(null);
      return;
    }
    console.info('[AoA] SimulationPanelView load/refresh', {
      evalConversationId,
      simulationRefreshNonce,
    });
    let cancelled = false;
    (async () => {
      try {
        const fetched = await conversationService.getMessages(evalConversationId);
        if (cancelled) return;
        console.info('[AoA] SimulationPanelView messages settled', {
          count: fetched.length,
        });
        void isPendingPlaceholder; /* explicit dep — re-runs the effect when the placeholder→real id transition happens */
        // Identity-preserving merge: keep the EXISTING message object
        // refs for any message_id that the server returned with the
        // same content. Without this, every refetch produces a brand-
        // new array of brand-new objects → React reconciler still
        // re-renders every MessageItem even though keys are stable →
        // the user perceives a flicker on each refresh. By reusing
        // refs we get a "diff and append" behaviour — old bubbles
        // don't redraw, new ones slide in.
        setMessages((prev) => {
          const prevById = new Map(prev.map((m) => [m.message_id, m]));
          const serverUserContents = new Set(
            fetched.filter((m) => m.role === 'user').map((m) => m.content.trim()),
          );
          // Index optimistic (sim-temp-*) user bubbles by trimmed
          // content. When the server confirms one, we copy the
          // optimistic's `render_key` onto the persisted row so the
          // React component (MessageList keys on `render_key ??
          // message_id`) stays MOUNTED across the temp→persisted
          // swap. Without this, the optimistic bubble unmounts the
          // moment the server echoes it and a brand-new one mounts
          // — visually that reads as the user message "rendering
          // again" right before the assistant reply lands.
          // (User flagged 2026-06-12.)
          const optimisticUsersByContent = new Map<string, Message>();
          for (const m of prev) {
            if (m.role === 'user' && m.message_id.startsWith('sim-temp-')) {
              optimisticUsersByContent.set(m.content.trim(), m);
            }
          }
          const consumedOptimisticIds = new Set<string>();
          const next: Message[] = [];
          for (const fm of fetched) {
            const existing = prevById.get(fm.message_id);
            if (existing && existing.content === fm.content && existing.role === fm.role) {
              // Same id + identical content — reuse the existing object
              // so React's referential check short-circuits the render.
              next.push(existing);
              continue;
            }
            if (fm.role === 'user') {
              const optimistic = optimisticUsersByContent.get(fm.content.trim());
              if (optimistic && !consumedOptimisticIds.has(optimistic.message_id)) {
                consumedOptimisticIds.add(optimistic.message_id);
                next.push({
                  ...fm,
                  // Carry the optimistic's stable render_key — that's
                  // the prop MessageList uses as React key, so the
                  // bubble component does NOT unmount.
                  render_key: optimistic.render_key ?? optimistic.message_id,
                });
                continue;
              }
            }
            next.push(fm);
          }
          // Append optimistic placeholders the server hasn't persisted
          // yet (e.g. user's in-flight typing or the AoA-driven message
          // for which we set toolInFlight=true). Drop ones already
          // consumed above as render_key donors, or ones whose content
          // has now been echoed by the server.
          for (const m of prev) {
            if (
              m.message_id.startsWith('sim-temp-') &&
              m.message_id !== AOA_DRAFT_ID &&
              !consumedOptimisticIds.has(m.message_id) &&
              !serverUserContents.has(m.content.trim())
            ) {
              next.push(m);
            }
          }
          return next;
        });
        setLoadError(null);
        // Clear the in-flight state ATOMICALLY with the new messages
        // landing in state. Doing this earlier (in dispatchEvent) created
        // a brief window where the typing indicator vanished but the new
        // bubbles hadn't arrived yet — the panel looked like it "reloaded"
        // because the old messages stood alone for a moment. Now the
        // indicator stays on until the freshly-persisted reply is on
        // screen, and the transition is a clean append, not a flash.
        setToolInFlight(evalConversationId, false);
        clearPendingUserMessage(evalConversationId);
        clearSimulationDraft(evalConversationId);
      } catch (e) {
        if (cancelled) return;
        console.error('[AoA] SimulationPanelView failed to load eval messages', e);
        setLoadError(
          e instanceof Error ? e.message : t('metaAgent.couldNotLoadSimulation'),
        );
      } finally {
        // Always settle loading. Fixes a StrictMode race where the
        // second mount's effect skipped this and left the panel stuck
        // on the spinner.
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setToolInFlight/clearPendingUserMessage are stable callbacks from the context
  }, [evalConversationId, simulationRefreshNonce, targetChatbotId, targetAgentName]);

  // Auto-scroll only when the message LIST GROWS (a new bubble landed).
  // Skipping this on every render — including on toolInFlight flips and
  // refetches that just preserved existing refs — was making the panel
  // feel like it "reloads" each time a turn completes; the snap to the
  // bottom paired with a new array reference reads as a re-render even
  // when individual MessageItems were memo-skipped.
  const prevMessageCountRef = useRef(0);
  useEffect(() => {
    if (messages.length > prevMessageCountRef.current) {
      const el = scrollerRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    }
    prevMessageCountRef.current = messages.length;
  }, [messages.length]);

  // Separately, while the user's own ChatInput is streaming a reply into
  // the panel (their typed message, not an AoA-driven one), keep nudging
  // the scroll as content grows inside the placeholder bubble.
  useEffect(() => {
    if (!streaming) return;
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [streaming, messages]);

  // Phase 3 — scroll-to-target. ONE-SHOT: when the panel first loads
  // (or the user clicks a different tool-call to jump to a different
  // message), scroll the matching bubble into view and pulse a
  // highlight. After that, never re-scroll to it — subsequent turns
  // belong at the bottom of the transcript, not back at the original
  // click target. User flagged 2026-06-12: "when I send a message, it
  // always highlights and scroll up to the last message that the
  // agent wrote ... it always goes up and highlight a message that is
  // irrelevant anymore."
  const handledScrollIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (loading) return;
    if (!scrollToMessageId) return;
    if (handledScrollIdRef.current === scrollToMessageId) return;
    const root = scrollerRef.current;
    if (!root) return;
    const el = root.querySelector<HTMLElement>(`[data-message-id="${scrollToMessageId}"]`);
    if (!el) return;
    // Mark handled BEFORE the timer so re-renders triggered by the
    // class addition don't queue another scroll.
    handledScrollIdRef.current = scrollToMessageId;
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('sim-highlight');
    const timer = window.setTimeout(() => el.classList.remove('sim-highlight'), 1800);
    return () => window.clearTimeout(timer);
  }, [loading, scrollToMessageId, messages.length]);

  // Cancel any in-flight stream on unmount so a parent remount doesn't
  // leak a half-consumed reader.
  useEffect(() => () => {
    if (streamCancelRef.current) {
      try { streamCancelRef.current(); } catch { /* ignore */ }
      streamCancelRef.current = null;
    }
  }, []);

  /**
   * Send a message to the target agent via the regular conversations
   * stream endpoint. This is the same pipeline the normal chat page
   * uses, so all of the target agent's behaviour (skills, modes,
   * provider routing) applies automatically.
   */
  const handleSend = useCallback(async (content: string, _attachments?: File[], overrideEvalId?: string) => {
    if (!content.trim() || streaming) return;
    // In setup mode the panel has no eval yet — the caller (runSetup) creates
    // one and passes its id here so the turn streams into the right eval.
    const eid = overrideEvalId || evalConversationId;
    if (!eid) return;
    // `pending-<chatbot>` is a client-side placeholder used while the
    // meta-agent's tool is still creating the real eval. Posting it sends that
    // string as a conversation_id and Postgres rejects it as an invalid uuid —
    // the panel then rendered the raw SQL error to the user (seen 2026-07-27).
    // The composer is disabled in this window; this is the guard behind it.
    if (eid.startsWith('pending-')) return;
    setStreaming(true);

    // Optimistically render the user bubble. The backend echoes back a
    // `user_message` event with the persisted row, so we swap on that
    // event — but the user sees their message instantly either way.
    const userTempId = `sim-temp-user-${Date.now()}`;
    const userMsg: Message = {
      message_id: userTempId,
      conversation_id: eid,
      role: 'user',
      content,
      created_at: new Date().toISOString(),
      render_key: userTempId,
    };
    // Streaming placeholder for the assistant reply.
    const aiPlaceholder: Message = {
      message_id: TEMP_AI_ID,
      conversation_id: eid,
      role: 'assistant',
      content: '',
      created_at: new Date().toISOString(),
      render_key: `sim-temp-ai-${Date.now()}`,
    };
    setMessages((prev) => [...prev, userMsg, aiPlaceholder]);

    // Build a cancel-able stream. The AsyncGenerator's reader is closed
    // automatically on early return, so .return() suffices to abort.
    let cancelled = false;
    streamCancelRef.current = () => { cancelled = true; };

    try {
      const stream = conversationService.streamMessage(eid, {
        content,
        // The eval conversation is already bound to its chatbot. We
        // pass chatbot_id explicitly as a belt-and-braces in case the
        // conversation row's chatbot field is null on edge-case eval
        // conversations created by older AoA runs.
        chatbot_id: targetChatbotId || undefined,
        // Pass the user's selected model FAMILY from the panel's picker.
        // When empty (user never picked) we omit so the target agent's
        // own default wins. The backend resolves the family key (or a
        // legacy concrete id) to the tenant-pinned version + provider.
        model: selectedModel || undefined,
      });

      for await (const ev of stream) {
        if (cancelled) break;
        if (ev.type === 'user_message') {
          // Replace optimistic user message with the persisted version
          // so its message_id is canonical (needed for edit/delete /
          // scroll-to-message anchoring).
          const persisted = ev.message;
          setMessages((prev) =>
            prev.map((m) =>
              m.message_id === userTempId
                ? {
                    ...m,
                    message_id: persisted.message_id,
                    created_at: persisted.created_at,
                    // backend message uses message_content; if it's
                    // populated, prefer it (handles the case where the
                    // backend trimmed / normalised the text).
                    content: (persisted as any).message_content || m.content,
                  }
                : m,
            ),
          );
        } else if (ev.type === 'ai_chunk' || ev.type === 'content') {
          const chunk = ev.type === 'ai_chunk'
            ? ev.content
            : (ev as { content: string }).content;
          setMessages((prev) =>
            prev.map((m) =>
              m.message_id === TEMP_AI_ID
                ? { ...m, content: (m.content || '') + chunk }
                : m,
            ),
          );
        } else if (ev.type === 'ai_complete') {
          const final = ev.message;
          if (final) {
            setMessages((prev) =>
              prev.map((m) =>
                m.message_id === TEMP_AI_ID
                  ? {
                      ...m,
                      message_id: final.message_id,
                      content: (final as any).message_content || m.content,
                      model_name: (final as any).message_metadata?.model || m.model_name,
                      mode_label: (final as any).message_metadata?.mode_label || m.mode_label,
                      active_skills: (final as any).message_metadata?.active_skills || m.active_skills,
                      created_at: final.created_at,
                    }
                  : m,
              ),
            );
          }
        } else if (ev.type === 'error') {
          setMessages((prev) =>
            prev.map((m) =>
              m.message_id === TEMP_AI_ID
                ? { ...m, content: m.content + `\n\n_${ev.error || ev.message || t('metaAgent.streamError')}_` }
                : m,
            ),
          );
        } else if (ev.type === 'done') {
          break;
        }
      }
    } catch (e) {
      // Surface a friendly inline error in the assistant bubble rather
      // than blowing up — the user can retry from the input.
      setMessages((prev) =>
        prev.map((m) =>
          m.message_id === TEMP_AI_ID
            ? { ...m, content: m.content || `_Failed: ${(e as Error)?.message || 'stream error'}_` }
            : m,
        ),
      );
    } finally {
      streamCancelRef.current = null;
      setStreaming(false);
    }
    // `selectedModel` IS in the dep array on purpose — without it,
    // this useCallback closes over the initial empty `selectedModel`
    // and the user's mid-conversation model pick never reaches the
    // request body, so the backend resolves the bot's default and
    // the picker visibly "snaps back" after send. User flagged
    // 2026-06-12: "I switched to a different model from the typing
    // bar, and then it switched it back after I sent the message."
  }, [evalConversationId, streaming, targetChatbotId, selectedModel]);

  const handleStopGeneration = useCallback(() => {
    if (streamCancelRef.current) {
      streamCancelRef.current();
      streamCancelRef.current = null;
    }
    setStreaming(false);
  }, []);

  const headerHint = useMemo(() => {
    if (loading) return t('common.loading');
    if (streaming || toolInFlight) {
      return agentKnown ? t('metaAgent.simulation.agentReplying', { name: targetAgentName }) : '';
    }
    return agentKnown ? t('metaAgent.simulation.evalWith', { name: targetAgentName }) : '';
  }, [loading, streaming, toolInFlight, agentKnown, targetAgentName, t]);

  // Mirror the AoA-driven pending user message INTO `messages` state
  // (not just into `displayMessages`). Why: when the refetch lands and
  // the server-persisted user row arrives, the merge above carries
  // the optimistic's `render_key` onto the persisted row, so the
  // bubble component stays mounted — only the assistant reply
  // appears as new. If the optimistic lived only in `displayMessages`
  // there'd be no `prev` entry for the merge to find, and the
  // persisted row would mount fresh (the "whole turn re-renders"
  // visual the user flagged 2026-06-12).
  useEffect(() => {
    if (!aoaPendingUserMessage) return;
    const trimmed = aoaPendingUserMessage.trim();
    if (!trimmed) return;
    setMessages((prev) => {
      // Don't push duplicates if this content already shows up
      // anywhere (server-persisted from a previous turn, or an
      // already-staged optimistic bubble for the same AoA call).
      const alreadyPresent = prev.some(
        (m) => m.role === 'user' && m.content.trim() === trimmed,
      );
      if (alreadyPresent) return prev;
      const optimisticId = `sim-temp-user-aoa-${evalConversationId}-${prev.length}`;
      const optimistic: Message = {
        message_id: optimisticId,
        conversation_id: evalConversationId,
        role: 'user',
        content: aoaPendingUserMessage,
        created_at: new Date().toISOString(),
        render_key: optimisticId,
      };
      return [...prev, optimistic];
    });
  }, [aoaPendingUserMessage, evalConversationId]);

  // The AoA-driven reply, live: one assistant bubble that grows with every
  // chunk. It reads like a real chat instead of a typing indicator for the
  // whole turn, and it is what makes a 20-second reply feel like a reply.
  useEffect(() => {
    if (!aoaDraft) {
      setMessages((prev) => (prev.some((m) => m.message_id === AOA_DRAFT_ID)
        ? prev.filter((m) => m.message_id !== AOA_DRAFT_ID)
        : prev));
      return;
    }
    setMessages((prev) => {
      const idx = prev.findIndex((m) => m.message_id === AOA_DRAFT_ID);
      if (idx === -1) {
        const bubble: Message = {
          message_id: AOA_DRAFT_ID,
          conversation_id: evalConversationId,
          role: 'assistant',
          content: aoaDraft,
          created_at: new Date().toISOString(),
          render_key: AOA_DRAFT_ID,
        };
        return [...prev, bubble];
      }
      const next = [...prev];
      next[idx] = { ...prev[idx], content: aoaDraft };
      return next;
    });
  }, [aoaDraft, evalConversationId]);

  // `displayMessages` is now just `messages`. The optimistic AoA bubble
  // is already inside `messages` (mirrored above), and the typing
  // indicator below the list is what tells the user the assistant is
  // mid-reply — no synthesized bubble needed.
  const displayMessages = messages;

  // Bidirectional model sync. Whenever the assistant lands a new
  // turn (whether it came from an AoA `send_test_message_to_agent`
  // call OR the user typed into the panel), pick up its `model_name`
  // and make it the picker's selected model.
  //
  // Subtle: depend on the LAST ASSISTANT MESSAGE'S model_name + id
  // explicitly, not the whole `messages` array. The array changes
  // every streaming chunk (immutable updates); re-running this
  // useEffect on every chunk did set the right value, but the
  // `selectedModel === lastAssistantModel` guard wasn't enough to
  // prevent stomping the user's pick mid-stream. By depending on
  // (id, model_name) we only re-sync when a NEW assistant message
  // arrives or its model_name is set for the first time.
  const lastAssistantSig = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m.role === 'assistant' && m.model_name) {
        return `${m.message_id}::${m.model_name}`;
      }
    }
    return null;
  }, [messages]);
  const lastSyncedSigRef = useRef<string | null>(null);
  useEffect(() => {
    if (!lastAssistantSig) return;
    const newModel = lastAssistantSig.split('::')[1];
    if (!newModel) return;
    // `model_name` is the CONCRETE model that ran — normalize it back to
    // its family key so it matches the family entries in the picker.
    // (Unmatched refs pass through as-is so an old eval on a retired
    // version still shows something rather than crashing.)
    //
    // `normalizeModelRef` IS a dep: the catalog loads async, so the
    // first sync may run before it's available and needs a re-run once
    // it lands. That late re-run must NOT stomp a pick the user made in
    // the meantime for the SAME assistant turn — hence lastSyncedSigRef.
    if (lastSyncedSigRef.current === lastAssistantSig && userPickedModelRef.current) return;
    lastSyncedSigRef.current = lastAssistantSig;
    const family = normalizeModelRef(newModel);
    if (family === selectedModel) return;
    setSelectedModel(family);
    userPickedModelRef.current = false;
    // selectedModel intentionally excluded from deps — we want to
    // re-sync only when a new assistant landed (lastAssistantSig
    // changed), not when our own setSelectedModel just ran.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lastAssistantSig, normalizeModelRef]);

  // Wrap the picker's onChange so we can record that the change came
  // from the user (not from the auto-sync above). On their next send,
  // their pick travels in the request, the backend pins it into
  // conversation_context, and the NEXT AoA turn inherits it.
  const handleModelPick = useCallback((model: string) => {
    userPickedModelRef.current = true;
    setSelectedModel(model);
  }, []);

  // The picker MUST show only the agent's own allowed_models —
  // never force-include an out-of-bounds model just because the
  // backend let one through. User flagged 2026-06-12: "agent of
  // agents won't have the ability to use a model inside a
  // conversation with an agent that is not allowed for this
  // particular agent." The dropdown is a contract: if it's not in
  // the agent's allowed_models, it cannot be picked, full stop.
  // The backend is now responsible for ALWAYS persisting an
  // assistant message with a model_name inside that allowed set
  // (eval_tools rejects AoA model overrides outside the set and
  // substitutes when no override was supplied).
  const effectiveAllowedModels = agentAllowedModels;

  return (
    <div className="sim-panel">
      {/* Mirror the regular chat-page header: target agent's avatar
          + name. Avatars are removed from individual messages below
          (hidden via CSS) so the agent's identity lives ONCE at the top
          of the panel, not on every reply bubble.

          The inspector-level header (Simulation · X) is hidden when a
          simulation is active to avoid a duplicated title — so the
          queue chip and collapse button live HERE instead. */}
      <div className="sim-panel__agent-header">
        {agentKnown
          ? <BotAvatar name={targetAgentName} size={32} />
          : <span className="sim-panel__agent-avatar-pending" aria-hidden />}
        <div className="sim-panel__agent-meta">
          <div dir="auto" className="sim-panel__agent-name">{displayAgentName}</div>
          <div dir="auto" className="sim-panel__agent-sub" aria-live="polite">
            <span className="sim-panel__agent-dot" data-streaming={(streaming || toolInFlight) || undefined} aria-hidden />
            {headerHint}
          </div>
        </div>
        <div className="sim-panel__agent-actions">
          {queueLen > 0 && (
            <button
              type="button"
              className={`aoa-inspector__queue-chip ${queueOpen ? 'is-open' : ''}`}
              onClick={() => setQueueOpen((o) => !o)}
              aria-label={t('counts.moreCardsWaiting', { count: queueLen })}
              data-testid="aoa-inspector-queue-chip"
            >
              <span className="aoa-inspector__queue-dot" aria-hidden="true" />
              {queueLen}
            </button>
          )}
          <button
            type="button"
            className="sim-panel__collapse-btn"
            onClick={toggleCollapsed}
            aria-label={t('metaAgent.inspector.collapsePanel')}
            title={t('metaAgent.inspector.collapsePanel')}
          >
            <svg className="rtl-flip" width="14" height="14" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tenant picker (running mode) — switch which tenant this agent is
          simulated under; changing it starts a fresh, tenant-bound run. The
          same hierarchical picker used by the "share agent to tenant" modal.
          Shows the tenant chosen on the card that started this run — the value
          is resolved from the eval's own sim_tenant_id, so the card and this
          bar can never disagree. */}
      {targetChatbotId && simTenants.length > 1 && (
        <div className="sim-panel__tenant-bar">
          <span className="sim-panel__tenant-label">{t('metaAgent.simulation.simulatingTenant')}</span>
          <div className="sim-panel__tenant-picker">
            <TenantHierarchyPicker
              tenants={simTenants}
              value={selectedTenantId}
              onChange={handleTenantChange}
              disabled={switchingTenant || streaming}
              placeholder={t('metaAgent.simulation.chooseTenant')}
            />
          </div>
        </div>
      )}

      {/* Queue peek — same shape as AoaInspectorPanel renders for
          proposals. Lets the user jump to a queued proposal without
          having to close the simulation first. */}
      {queueOpen && queueLen > 0 && (
        <div className="aoa-inspector__queue-peek">
          <div className="aoa-inspector__queue-peek-title">{t('metaAgent.inspector.upNext')}</div>
          <ul className="aoa-inspector__queue-list">
            {proposalQueue.map((p) => (
              <li key={p.proposal_id}>
                <button
                  type="button"
                  className="aoa-inspector__queue-item"
                  onClick={() => { setActiveProposal(p); setQueueOpen(false); }}
                >
                  <span className="aoa-inspector__queue-item-tool" dir="auto">
                    {p.tool_name.replace(/^propose_/, '').replace(/_/g, ' ')}
                  </span>
                  {p.rationale && (
                    <span className="aoa-inspector__queue-item-rationale" dir="auto">
                      {p.rationale.length > 60 ? p.rationale.slice(0, 60) + '…' : p.rationale}
                    </span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="sim-panel__body" ref={scrollerRef}>
        {loadError ? (
          <div className="sim-panel__error">{loadError}</div>
        ) : loading ? (
          <div className="sim-panel__loading">{t('common.loading') || 'Loading…'}</div>
        ) : displayMessages.length === 0 ? (
          <div className="sim-panel__empty">
            <div className="sim-panel__empty-title">{t('metaAgent.simulation.noMessagesYet')}</div>
            <div className="sim-panel__empty-sub">
              {t('metaAgent.simulation.typeMessageToTalkPrefix')}<strong>{targetAgentName}</strong>{t('metaAgent.simulation.typeMessageToTalkSuffix')}
            </div>
          </div>
        ) : (
          <>
            <MessageList
              messages={displayMessages}
              streaming={streaming || toolInFlight}
              botName={targetAgentName}
              /* Skill badges stay ON — they're a small chip showing
               * which skill the agent activated (useful for the
               * auditor). The "thinking" banner is OFF — user asked
               * 2026-06-12 to revert the reveal_thinking force-on
               * for eval conversations; the panel should look like a
               * regular chat with no reasoning monologue. */
              showSkillLabels
            />
            {toolInFlight && !aoaDraft && (
              <div className="sim-panel__typing" aria-live="polite">
                <span className="sim-panel__typing-dot" />
                <span className="sim-panel__typing-dot" />
                <span className="sim-panel__typing-dot" />
                <span className="sim-panel__typing-text">
                  {t('metaAgent.simulation.agentReplying', { name: targetAgentName })}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      <div className="sim-panel__input">
        {/* `aoa-input-frame` wrapper — same one AoAChatPanel uses
            around the main ChatInput. Gives the bar a max-width and
            auto-centers it so the visual treatment is the SAME as the
            main: same horizontal inset, same centering behaviour,
            same clamp at wide widths. Without this wrapper the bar
            spread edge-to-edge of the panel and read as "very big"
            next to the main bar — user flagged 2026-06-12. */}
        <div className="aoa-input-frame">
        <ChatInput
          /* Third positional arg is the composer's Drive picks, NOT an eval
             override — bind only the two this surface uses. The simulation
             panel does not carry Drive picks through to a turn, so the Drive
             entry is switched off here rather than offered and dropped. */
          onSendMessage={(content, attachments) => { void handleSend(content, attachments); }}
          driveAttachments={false}
          /* No real eval row yet — the meta-agent's tool is still creating it.
             Sending now would post the `pending-…` placeholder as a
             conversation_id and surface a database error in the panel. */
          textareaDisabled={isPendingPlaceholder}
          selectedModel={selectedModel}
          /* Wrapped picker callback. `handleModelPick` flags the
           * change as user-initiated so the auto-sync effect won't
           * stomp it before the next assistant turn lands. */
          onModelChange={handleModelPick}
          /* Picker is driven by the agent's own allowed_models (loaded
             above via botService.getBot). ChatInput intersects this
             with the tenant's allowed_models when it queries
             /ai/models/, so the dropdown shows exactly what the
             Edit-Agent UI shows — the agent ∩ tenant set.

             Voice input ("speak to text") is gated by the tenant's
             `chat.voice_input` feature flag inside ChatInput itself
             (useFeature('chat.voice_input')); no extra prop needed —
             it surfaces automatically when the flag is on.

             Attachments are ON: the panel and the main AoA input must
             render identical chrome (paperclip + sparkles + mic +
             send). User flagged 2026-06-12 that disabling attachments
             on the panel made the two bars look mismatched —
             different icon row, different visual weight. The eval
             conversation uses the same /conversations/<id>/stream
             endpoint as a normal chat, so attachment upload Just
             Works without any special wiring here. */
          allowedModels={effectiveAllowedModels}
          // The simulated tenant's pins name the models (the run answers on
          // them — backend model_labels.pins_tenant_id).
          modelCatalogContext={isPendingPlaceholder ? undefined : { conversationId: evalConversationId }}
          isStreaming={streaming}
          onStopGeneration={handleStopGeneration}
        />
        </div>
      </div>
    </div>
  );
};
